import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS, STAFF_ROLES, ORG_ROLES, roleOf, fmtDate } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { CopyLink } from "@/components/app/CopyLink";
import { InviteForm } from "@/components/app/InviteForm";

const btn = "rounded-full border border-gray-300 px-3 py-1 text-xs text-oath-ink hover:bg-gray-50";

const UserRow = ({ u, self, onChanged, onLink }) => {
  const act = async (path, body, msg) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/${path}`, body);
      toast.success(msg);
      if (data.reset_path) onLink({ email: u.email, path: data.reset_path, kind: "Password reset" });
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <tr className="border-b border-gray-100 last:border-0" data-testid={`user-row-${u.id}`}>
      <td className="px-4 py-3"><div className="font-medium text-oath-ink">{u.name}</div><div className="text-xs text-oath-secondary">{u.email}</div></td>
      <td className="px-4 py-3 text-sm">{ROLE_LABELS[roleOf(u)]}<div className="text-xs text-oath-muted">{u.org_name || "Staff"}</div></td>
      <td className="px-4 py-3 font-mono-label text-[9px]">
        <span className={u.status === "active" ? "text-emerald-700" : "text-red-600"}>{u.status.toUpperCase()}</span>
        <div className={u.totp_enabled ? "text-emerald-700" : "text-amber-700"}>{u.totp_enabled ? "2FA ON" : "2FA PENDING"}</div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-oath-secondary">{fmtDate(u.last_login_at)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          <button className={btn} onClick={() => act("reset-2fa", {}, "2FA reset — user will set it up again")} data-testid={`user-reset-2fa-${u.id}`}>Reset 2FA</button>
          <button className={btn} onClick={() => act("password-reset", {}, "Reset link created")} data-testid={`user-reset-password-${u.id}`}>Reset link</button>
          {!self && (
            <button className={btn} onClick={() => act("status", { status: u.status === "active" ? "disabled" : "active" }, "Status updated")} data-testid={`user-toggle-status-${u.id}`}>
              {u.status === "active" ? "Disable" : "Enable"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const PendingInvites = ({ invites, orgs, onChanged, onLink }) => {
  const orgName = (id) => orgs.find((o) => o.id === id)?.name;
  const regen = async (inv) => {
    try {
      const { data } = await api.post(`/admin/invites/${inv.id}/regenerate`);
      onLink({ email: inv.email, path: data.invite_path, kind: "Invite" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const revoke = async (inv) => {
    try {
      await api.delete(`/admin/invites/${inv.id}`);
      toast.success("Invite revoked");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (!invites.length) return <p className="text-sm text-oath-muted" data-testid="invites-empty">No pending invites.</p>;
  return (
    <div className="grid gap-2" data-testid="invites-list">
      {invites.map((i) => (
        <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid={`invite-row-${i.id}`}>
          <div className="text-sm">
            <span className="font-medium text-oath-ink">{i.email}</span>
            <span className="text-oath-secondary"> · {ROLE_LABELS[i.platform_role || i.org_role]}{i.org_id ? ` · ${orgName(i.org_id) || ""}` : ""}</span>
            <div className="font-mono text-[11px] text-oath-muted">expires {fmtDate(i.expires_at)}</div>
          </div>
          <div className="flex gap-1">
            <button className={btn} onClick={() => regen(i)} data-testid={`invite-regenerate-${i.id}`}>New link</button>
            <button className={btn} onClick={() => revoke(i)} data-testid={`invite-revoke-${i.id}`}>Revoke</button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [link, setLink] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, i, o] = await Promise.all([api.get("/admin/users"), api.get("/admin/invites"), api.get("/admin/orgs")]);
      setData({ users: u.data, invites: i.data, orgs: o.data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <AppShell eyebrow="— SUPER ADMIN" title="Users & invites"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></AppShell>;

  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Users & invites">
      <InviteForm roles={[...STAFF_ROLES, ...ORG_ROLES]} orgs={data.orgs} endpoint="/admin/invites" onCreated={load} testid="admin-invite" />
      {link && (
        <div className="mt-4 rounded-2xl border border-oath-accent/30 bg-oath-accent-light p-4" data-testid="admin-generated-link">
          <p className="mb-2 text-xs text-oath-secondary">{link.kind} link for {link.email}:</p>
          <CopyLink path={link.path} testid="admin-generated" />
        </div>
      )}
      <h2 className="mt-10 font-display text-lg font-bold text-oath-ink">Pending invites</h2>
      <div className="mt-3"><PendingInvites invites={data.invites} orgs={data.orgs} onChanged={load} onLink={setLink} /></div>
      <h2 className="mt-10 font-display text-lg font-bold text-oath-ink">Users</h2>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="w-full min-w-[820px] text-left" data-testid="users-table">
          <thead className="border-b border-gray-200 bg-oath-bg/60 font-mono-label text-[9px] text-oath-muted">
            <tr><th className="px-4 py-3">USER</th><th className="px-4 py-3">ROLE</th><th className="px-4 py-3">STATUS</th><th className="px-4 py-3">LAST SIGN-IN</th><th className="px-4 py-3">ACTIONS</th></tr>
          </thead>
          <tbody>
            {data.users.map((u) => <UserRow key={u.id} u={u} self={u.id === user.id} onChanged={load} onLink={setLink} />)}
          </tbody>
        </table>
      </div>
      <h2 className="mt-10 font-display text-lg font-bold text-oath-ink">Client companies</h2>
      <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="orgs-list">
        {data.orgs.map((o) => (
          <div key={o.id} className="rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid={`org-row-${o.id}`}>
            <div className="font-medium text-oath-ink">{o.name}</div>
            <div className="text-xs text-oath-secondary">{o.industry} · {o.user_count} user{o.user_count === 1 ? "" : "s"} · created {fmtDate(o.created_at)}</div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
